import React from 'react';
import { KPI } from '../utils/kpiAnalyzer';
import './KPIDashboard.css';

interface KPIDashboardProps {
  kpis: KPI[];
  fileName: string;
}

const KPIDashboard: React.FC<KPIDashboardProps> = ({ kpis, fileName }) => {
  const getTrendIcon = (trend?: KPI['trend']) => {
    switch (trend) {
      case 'up':
        return (
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="23,6 13.5,15.5 8.5,10.5 1,18"/>
            <polyline points="17,6 23,6 23,12"/>
          </svg>
        );
      case 'down':
        return (
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="23,18 13.5,8.5 8.5,13.5 1,6"/>
            <polyline points="17,18 23,18 23,12"/>
          </svg>
        );
      case 'stable':
        return (
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="5" y1="12" x2="19" y2="12"/>
          </svg>
        );
      default:
        return null;
    }
  };

  const getTrendColor = (trend?: KPI['trend']) => {
    switch (trend) {
      case 'up': return '#10b981';
      case 'down': return '#ef4444';
      case 'stable': return '#6b7280';
      default: return 'transparent';
    }
  };

  const getCategoryBadgeColor = (category: KPI['category']) => {
    switch (category) {
      case 'financial': return '#10b981';
      case 'performance': return '#6366f1';
      case 'volume': return '#8b5cf6';
      case 'quality': return '#f59e0b';
      case 'distribution': return '#06b6d4';
      default: return '#6b7280';
    }
  };

  if (kpis.length === 0) {
    return (
      <div className="kpi-dashboard">
        <div className="kpi-header">
          <h3>Key Performance Indicators</h3>
          <span className="kpi-subtitle">Analyzing {fileName}...</span>
        </div>
        <div className="kpi-loading">
          <div className="loading-spinner"></div>
          <span>Calculating KPIs...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="kpi-dashboard">
      <div className="kpi-header">
        <div className="kpi-title-section">
          <h3>Key Performance Indicators</h3>
          <span className="kpi-subtitle">{fileName} • Top business metrics</span>
        </div>
        <div className="kpi-refresh">
          <span className="refresh-text">Auto-updated</span>
          <div className="refresh-indicator"></div>
        </div>
      </div>

      <div className="kpi-grid">
        {kpis.map((kpi, index) => (
          <div 
            key={kpi.id} 
            className="kpi-card"
            style={{ animationDelay: `${index * 0.1}s` }}
          >
            <div className="kpi-card-header">
              <div className="kpi-icon-section">
                <div 
                  className="kpi-icon"
                  style={{ backgroundColor: `${kpi.color}15`, color: kpi.color }}
                >
                  {kpi.icon}
                </div>
                <div 
                  className="kpi-category-badge"
                  style={{ backgroundColor: getCategoryBadgeColor(kpi.category) }}
                >
                  {kpi.category}
                </div>
              </div>
              
              {kpi.trend && (
                <div 
                  className="kpi-trend"
                  style={{ color: getTrendColor(kpi.trend) }}
                >
                  {getTrendIcon(kpi.trend)}
                  {kpi.trendValue && (
                    <span className="trend-value">{kpi.trendValue}</span>
                  )}
                </div>
              )}
            </div>

            <div className="kpi-content">
              <div className="kpi-value-section">
                <div 
                  className="kpi-value"
                  style={{ color: kpi.color }}
                >
                  {kpi.formattedValue}
                </div>
                <div className="kpi-title">{kpi.title}</div>
              </div>
              
              <div className="kpi-description">
                {kpi.description}
              </div>
            </div>

            <div className="kpi-type-indicator">
              <span className="type-badge">{kpi.type}</span>
            </div>

            <div className="kpi-priority-bar">
              <div 
                className="priority-fill"
                style={{ 
                  width: `${kpi.priority}%`,
                  backgroundColor: kpi.color 
                }}
              ></div>
            </div>
          </div>
        ))}
      </div>

      <div className="kpi-summary">
        <div className="summary-stats">
          <span className="summary-item">
            <strong>{kpis.filter(k => k.category === 'financial').length}</strong> Financial
          </span>
          <span className="summary-item">
            <strong>{kpis.filter(k => k.category === 'performance').length}</strong> Performance
          </span>
          <span className="summary-item">
            <strong>{kpis.filter(k => k.category === 'volume').length}</strong> Volume
          </span>
          <span className="summary-item">
            <strong>{kpis.filter(k => k.category === 'quality').length}</strong> Quality
          </span>
        </div>
        <div className="summary-note">
          📌 Top 4 business metrics automatically selected for optimal insights
        </div>
      </div>
    </div>
  );
};

export default KPIDashboard;